import { DEFAULT_ANALYSIS_SETTINGS } from '@/constants/default-analysis-settings';
import { UploadFile, QueueItem } from '@/types/source-modal';

export function generateFileId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

export function getFileFingerprint(file: File): string {
    return `${file.name}-${file.size}-${file.lastModified}`;
}

export async function getVideoDuration(file: File): Promise<number> {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';

        video.onloadedmetadata = () => {
            window.URL.revokeObjectURL(video.src);
            resolve(video.duration);
        };

        video.onerror = () => {
            window.URL.revokeObjectURL(video.src);
            reject(new Error('Failed to load video metadata'));
        };

        video.src = URL.createObjectURL(file);
    });
}

export function getSubmitButtonText(count: number): string {
    return count === 1 ? 'Add Source' : `Add ${count} Sources`;
}

export async function processFileForUpload(file: File): Promise<UploadFile> {
    const id = generateFileId();
    const previewUrl = URL.createObjectURL(file);
    let duration: number | undefined;
    let previewError = false;

    try {
        duration = await getVideoDuration(file);
    } catch (error) {
        console.warn(
            'Video preview unavailable - file will be processed after upload:',
            error,
        );
        previewError = true;
    }

    return {
        id,
        file,
        previewUrl,
        fileName: file.name,
        fileSize: file.size,
        duration,
        trimStart: 0,
        trimEnd: duration || 0,
        ...DEFAULT_ANALYSIS_SETTINGS,
        previewError,
    };
}

export async function processFilesForUpload(
    files: File[],
): Promise<UploadFile[]> {
    return Promise.all(files.map(processFileForUpload));
}

export function prepareFilesForQueue(files: UploadFile[]): QueueItem[] {
    return files.map((file) => ({
        fingerprint: getFileFingerprint(file.file),
        file: file.file,
        fileName: file.fileName,
        fileSize: file.fileSize,
        faceRecognition: file.faceRecognition,
        licensePlateRecognition: file.licensePlateRecognition,
        opticalCharacterRecognition: file.opticalCharacterRecognition,
        analysisFramesPerSecond: file.analysisFramesPerSecond,
    }));
}
